import * as Sentry from '@sentry/vue';
import Axios from 'axios';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { defineAsyncComponent, createApp } from 'vue';
import { HeartIcon } from '@heroicons/vue/24/outline';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover, PopoverButton, PopoverGroup, PopoverPanel,
  Listbox, ListboxButton, ListboxOption, ListboxOptions,
} from '@headlessui/vue';
import { LaravelRoutes, setLaravelRoutes } from './shared/helpers/routes';
import translate from './shared/helpers/translate';
import { lazyLoad } from './shared/directives/lazy-load.directive';
import { scrollEnd } from './shared/directives/scroll-end.directive';
import VHoverDelayedState from './components/state/VHoverDelayedState.vue';
import TheSearch from './features/search/components/TheSearch.vue';
import VIconCB from './icons/payment/VIconCB.vue';
import VIconPaypal from './icons/payment/VIconPaypal.vue';
import VIconVisa from './icons/payment/VIconVisa.vue';
import VIconMastercard from './icons/payment/VIconMastercard.vue';
import VIconBankwireDe from './icons/payment/VIconBankwireDe.vue';
import VIconBankwireFr from './icons/payment/VIconBankwireFr.vue';
import VIconBankwireEs from './icons/payment/VIconBankwireEs.vue';
import VIconBankwireIt from './icons/payment/VIconBankwireIt.vue';
import VIconAlma from './icons/payment/VIconAlma.vue';
import VIconFacebook from './icons/VIconFacebook.vue';
import VIconTwitter from './icons/VIconTwitter.vue';
import VIconInstagram from './icons/VIconInstagram.vue';
import VIconPinterest from './icons/VIconPinterest.vue';
import VIconYoutube from './icons/VIconYoutube.vue';
import VIconBlog from './icons/VIconBlog.vue';
import VIconDe from './icons/country/VIconDe.vue';
import VIconFr from './icons/country/VIconFr.vue';
import VIconIt from './icons/country/VIconIt.vue';
import VIconBe from './icons/country/VIconBe.vue';
import VIconLu from './icons/country/VIconLu.vue';
import VIconEs from './icons/country/VIconEs.vue';
import VIconChevronR from './icons/VIconChevronR.vue';
import VIconChevronD from './icons/VIconChevronD.vue';
import VIconChevronL from './icons/VIconChevronL.vue';
import VIconChevronU from './icons/VIconChevronU.vue';
import VIconX from './icons/VIconX.vue';
import TheMainMenuMobile from './components/menu/TheMainMenuMobile.vue';
import TheFooterMenu from './components/footer/TheFooterMenu.vue';
import TheWishlistAction from './components/product/TheWishlistAction.vue';
import VLazyImg from './components/common/VLazyImg.vue';
import VExpandableText from './components/common/VExpandableText.vue';
import TheAddToCart from './components/product/TheAddToCart.vue';
import TheAddRefurbishedToCart from './components/product/TheAddRefurbishedToCart.vue';
import VSlider from './components/common/VSlider.vue';
import TheProductQuantity from './components/product/TheProductQuantity.vue';
import TheProductQuantityOptions from './components/product/TheProductQuantityOptions.vue';
import VAlertSuccess from './components/alerts/VAlertSuccess.vue';
import TheProductCount from './features/productList/components/TheProductCount.vue';
// TODO do not write spagetti javascript
import './video-player';
import VIconSpinner from './icons/VIconSpinner.vue';
import VIconSpinnerSecondary from './icons/VIconSpinnerSecondary.vue';
import VIconSearch from './icons/VIconSearch.vue';
import TheProductList from './features/productList/TheProductList.vue';
import TheResumeQuote from './features/myAccount/orders/components/TheResumeQuote.vue';
import TheProductForGTM from './components/product/TheProductForGTM.vue';

/**
 * Async Components
 *
 * Need skeletons to be loaded before the app is mounted
 *
 * @see https://vuejs.org/guide/components/async
 */
const FidelityPointsUsagePopover = defineAsyncComponent(() => import('./components/customer/fidelityPointsUsagePopover.vue'));
const ProForm = defineAsyncComponent(() => import('./components/pro-form.vue'));
const Alert = defineAsyncComponent(() => import('./components/common/alert.vue'));
const BtnSubmit = defineAsyncComponent(() => import('./components/common/btnSubmit.vue'));
const InputError = defineAsyncComponent(() => import('./components/common/inputError.vue'));
const MarketingInfos = defineAsyncComponent(() => import('./components/common/marketingInfos.vue'));
const NewsletterEmail = defineAsyncComponent(() => import('./components/footer/newsletterEmail.vue'));
const TheProductPrice = defineAsyncComponent(() => import('./components/product/TheProductPrice.vue'));
const TheProductOptions = defineAsyncComponent(() => import('./components/product/TheProductOptions.vue'));
const TheOneySimulations = defineAsyncComponent(() => import('./components/product/TheOneySimulations.vue'));
const TheProductVariation = defineAsyncComponent(() => import('./components/product/TheProductVariation.vue'));
const TheProductDescription = defineAsyncComponent(() => import('./components/product/TheProductDescription.vue'));
const TheProductReviewList = defineAsyncComponent(() => import('./components/product/review/TheProductReviewList.vue'));
const TheProductCardReviewAvg = defineAsyncComponent(() => import('./components/product/TheProductCardReviewAvg.vue'));
const ProductCard = defineAsyncComponent(() => import('./features/productList/components/ProductCard.vue'));
const ProductCarousel = defineAsyncComponent(() => import('./components/product/ProductCarousel.vue'));
const TheProductImagesCarousel = defineAsyncComponent(() => import('./components/product/TheProductImagesCarousel.vue'));
const TheCollectionsCarousel = defineAsyncComponent(() => import('./components/homepage/TheCollectionsCarousel.vue'));
const TheInformationsDisclosure = defineAsyncComponent(() => import('./components/product/TheInformationsDisclosure.vue'));
const TheReconditionnedProducts = defineAsyncComponent(() => import('./components/product/TheReconditionnedProducts.vue'));
const TheProductReviewCreate = defineAsyncComponent(() => import('./components/product/review/TheProductReviewCreate.vue'));
const TheProductReviewNotAllowed = defineAsyncComponent(() => import('./components/product/review/TheProductReviewNotAllowed.vue'));
const TheProductReviewConfirmation = defineAsyncComponent(() => import('./components/product/review/TheProductReviewConfirmation.vue'));
const TheProductReviewMedias = defineAsyncComponent(() => import('./components/product/review/TheProductReviewMedias.vue'));
const TheProductReviewMediasDialog = defineAsyncComponent(() => import('./components/product/review/TheProductReviewMediasDialog.vue'));
const CartWrapper = defineAsyncComponent(() => import('./components/cart/cartWrapper.vue'));
const TheEmptyWishlist = defineAsyncComponent(() => import('./features/myAccount/wishlist/components/TheWishlistEmptyState.vue'));
const TheEmptyOrders = defineAsyncComponent(() => import('./features/myAccount/orders/components/TheOrdersEmptyState.vue'));
const TheFaqCategories = defineAsyncComponent(() => import('./components/faq/TheFaqCategories.vue'));
const TheFaqCategory = defineAsyncComponent(() => import('./components/faq/TheFaqCategory.vue'));
const TheNotification = defineAsyncComponent(() => import('./components/common/TheNotification.vue'));
const TheAftersaleCreate = defineAsyncComponent(() => import('./components/customer/aftersale/TheAftersaleCreate.vue'));
const TheAftersaleFollowUp = defineAsyncComponent(() => import('./components/customer/aftersale/TheAftersaleFollowUp.vue'));
const AftersaleListItem = defineAsyncComponent(() => import('./components/customer/aftersale/AftersaleListItem.vue'));
const AftersaleListEligibleItem = defineAsyncComponent(() => import('./components/customer/aftersale/AftersaleListEligibleItem.vue'));

/**
 * App creation
 *
 * Create vue pplication
 *
 * @see https://vuejs.org/guide/essentials/application
 */
const app = createApp();

/**
 * Component registration
 *
 * Register for blade files only otherwise use dynamic import
 *
 * @see https://vuejs.org/guide/components/registration
 */

/**
 * Headless UI
 */
app.component('popover-group', PopoverGroup);
app.component('popover', Popover);
app.component('popover-button', PopoverButton);
app.component('popover-panel', PopoverPanel);
app.component('disclosure', Disclosure);
app.component('disclosure-button', DisclosureButton);
app.component('disclosure-panel', DisclosurePanel);
app.component('listbox', Listbox);
app.component('listbox-button', ListboxButton);
app.component('listbox-option', ListboxOption);
app.component('listbox-options', ListboxOptions);

/**
 * State
 */
app.component('v-hover-delayed-state', VHoverDelayedState);

/**
 * Icones
 */
app.component('v-icon-chevron-r', VIconChevronR);
app.component('v-icon-chevron-d', VIconChevronD);
app.component('v-icon-chevron-l', VIconChevronL);
app.component('v-icon-chevron-u', VIconChevronU);
app.component('v-icon-x', VIconX);
app.component('v-icon-spinner', VIconSpinner);
app.component('v-icon-spinner-secondary', VIconSpinnerSecondary);
app.component('v-icon-search', VIconSearch);
app.component('v-icon-faq', defineAsyncComponent(() => import('./icons/VIconFaq.vue')));
app.component('v-icon-help', defineAsyncComponent(() => import('./icons/VIconHelp.vue')));
app.component('v-icon-cart', defineAsyncComponent(() => import('./icons/VIconCart.vue')));
app.component('v-icon-user', defineAsyncComponent(() => import('./icons/VIconUser.vue')));
app.component('v-icon-star', defineAsyncComponent(() => import('./icons/VIconStar.vue')));
app.component('v-icon-mail', defineAsyncComponent(() => import('./icons/VIconMail.vue')));
app.component('v-icon-check', defineAsyncComponent(() => import('./icons/VIconCheck.vue')));
app.component('v-icon-phone', defineAsyncComponent(() => import('./icons/VIconPhone.vue')));
app.component('v-icon-trash', defineAsyncComponent(() => import('./icons/VIconTrash.vue')));
app.component('v-icon-heart', defineAsyncComponent(() => import('./icons/VIconHeart.vue')));
app.component('v-icon-locked', defineAsyncComponent(() => import('./icons/VIconLocked.vue')));
app.component('v-logo-google', defineAsyncComponent(() => import('./icons/VLogoGoogle.vue')));
app.component('v-icon-photos', defineAsyncComponent(() => import('./icons/VIconPhotos.vue')));
app.component('v-icon-triman', defineAsyncComponent(() => import('./icons/VIconTriman.vue')));
app.component('v-icon-pencil', defineAsyncComponent(() => import('./icons/VIconPencil.vue')));
app.component('v-icon-filters', defineAsyncComponent(() => import('./icons/VIconFilters.vue')));
app.component('v-logo-facebook', defineAsyncComponent(() => import('./icons/VLogoFacebook.vue')));
app.component('v-icon-download', defineAsyncComponent(() => import('./icons/VIconDownload.vue')));
app.component('v-icon-arrow-left', defineAsyncComponent(() => import('./icons/VIconArrowLeft.vue')));
app.component('v-icon-arrow-right', defineAsyncComponent(() => import('./icons/VIconArrowRight.vue')));
app.component('v-icon-exclamation-triangle', defineAsyncComponent(() => import('./icons/VIconExclamationTriangle.vue')));
app.component('v-icon-minus', defineAsyncComponent(() => import('./icons/VIconMinus.vue')));
app.component('v-icon-plus', defineAsyncComponent(() => import('./icons/VIconPlus.vue')));
app.component('v-icon-quote-right', defineAsyncComponent(() => import('./icons/VIconQuoteRight.vue')));
app.component('v-icon-quote-left', defineAsyncComponent(() => import('./icons/VIconQuoteLeft.vue')));

/**
 * Payment methods icons
*/
app.component('v-icon-cb', VIconCB);
app.component('v-icon-visa', VIconVisa);
app.component('v-icon-paypal', VIconPaypal);
app.component('v-icon-mastercard', VIconMastercard);
app.component('v-icon-bankwire_de', VIconBankwireDe);
app.component('v-icon-bankwire_fr', VIconBankwireFr);
app.component('v-icon-bankwire_es', VIconBankwireEs);
app.component('v-icon-bankwire_it', VIconBankwireIt);
app.component('v-icon-alma', VIconAlma);

/**
 * Social network icons
*/
app.component('v-icon-facebook', VIconFacebook);
app.component('v-icon-twitter', VIconTwitter);
app.component('v-icon-instagram', VIconInstagram);
app.component('v-icon-pinterest', VIconPinterest);
app.component('v-icon-youtube', VIconYoutube);
app.component('v-icon-blog', VIconBlog);

/**
 * Country flags icons
*/
app.component('v-icon-de', VIconDe);
app.component('v-icon-fr', VIconFr);
app.component('v-icon-it', VIconIt);
app.component('v-icon-be', VIconBe);
app.component('v-icon-lu', VIconLu);
app.component('v-icon-es', VIconEs);

/**
 * Layout and related
 */
app.component('the-notification-banner', defineAsyncComponent(() => import('./components/notifications/TheNotificationBanner.vue')));

/**
 * Header
 */
app.component('the-main-menu-mobile', TheMainMenuMobile);
app.component('the-search', TheSearch);
app.component('the-action-wishlist', defineAsyncComponent(() => import('./components/header/TheActionWishlist.vue')));
app.component('the-action-help', defineAsyncComponent(() => import('./components/header/TheActionHelp.vue')));
app.component('the-action-user-account', defineAsyncComponent(() => import('./components/header/TheActionUserAccount.vue')));
app.component('the-action-cart', defineAsyncComponent(() => import('./components/header/TheActionCart.vue')));

/**
 * Footer
 */
app.component('the-footer-menu', TheFooterMenu);

/**
 * Auth Pages and related
 */
app.component('the-auth', defineAsyncComponent(() => import('./features/auth/components/TheAuth.vue')));
app.component('the-password-forgotten', defineAsyncComponent(() => import('./features/auth/components/ThePasswordForgotten.vue')));
app.component('the-password-reset', defineAsyncComponent(() => import('./features/auth/components/ThePasswordReset.vue')));

/**
 * Customer Pages and related
 */
app.component('the-sidebar', defineAsyncComponent(() => import('./components/customer/TheSidebar.vue')));
app.component('the-newsletter', defineAsyncComponent(() => import('./features/myAccount/newsletter/components/TheNewsletter.vue')));
app.component('the-account-information', defineAsyncComponent(() => import('./features/myAccount/information/components/TheAccountInformation.vue')));
app.component('the-address-list', defineAsyncComponent(() => import('./features/myAccount/address/components/TheAddressList.vue')));
app.component('the-address-edit', defineAsyncComponent(() => import('./features/myAccount/address/components/TheAddressEdit.vue')));
app.component('the-address-create', defineAsyncComponent(() => import('./features/myAccount/address/components/TheAddressCreate.vue')));
app.component('the-wishlist', defineAsyncComponent(() => import('./features/myAccount/wishlist/components/TheWishlist.vue')));
app.component('the-fidelity-points-history', defineAsyncComponent(() => import('./features/myAccount/fidelityPoints/components/TheFidelityPointsHistory.vue')));
app.component('the-prepaid-history', defineAsyncComponent(() => import('./features/myAccount/prepaid/components/ThePrepaidHistory.vue')));
app.component('the-sponsorship', defineAsyncComponent(() => import('./features/sponsorship/components/TheSponsorship.vue')));

/**
 * Checkout
 */
app.component('the-checkout', defineAsyncComponent(() => import('./features/checkout/components/TheCheckout.vue')));
app.component('the-payment-return', defineAsyncComponent(() => import('./features/checkout/payment/components/ThePaymentReturn.vue')));

/**
 * Product and related
*/
app.component('the-product-count', TheProductCount);
app.component('the-product-card-review-avg', TheProductCardReviewAvg);
app.component('product-card', ProductCard);
app.component('product-carousel', ProductCarousel);
app.component('the-product-list', TheProductList);
app.component('the-filters', defineAsyncComponent(() => import('./features/productList/components/TheFilters.vue')));
app.component('the-filters-dialog', defineAsyncComponent(() => import('./features/productList/components/TheFiltersDialog.vue')));

/**
 * Widget
 */
app.component('v-widget-alma', defineAsyncComponent(() => import('./components/widgets/VWidgetAlma.vue')));

/**
 * Commons components
 */
app.component('v-slider', VSlider);
app.component('v-lazy-img', VLazyImg);
app.component('v-expandable-text', VExpandableText);
app.component('alert', Alert);
app.component('btn-submit', BtnSubmit);
app.component('input-error', InputError);
app.component('marketing-infos', MarketingInfos);
app.component('newsletter-email', NewsletterEmail);
app.component('heart-icon-outline', HeartIcon);
app.component('the-wishlist-action', TheWishlistAction);
app.component('the-add-to-cart', TheAddToCart);
app.component('the-add-refurbished-to-cart', TheAddRefurbishedToCart);
app.component('the-product-price', TheProductPrice);
app.component('the-product-options', TheProductOptions);
app.component('the-product-quantity', TheProductQuantity);
app.component('the-product-quantity-options', TheProductQuantityOptions);
app.component('the-oney-simulations', TheOneySimulations);
app.component('the-product-variation', TheProductVariation);
app.component('the-product-review-list', TheProductReviewList);
app.component('the-product-description', TheProductDescription);
app.component('the-product-review-create', TheProductReviewCreate);
app.component('the-product-review-confirmation', TheProductReviewConfirmation);
app.component('the-product-review-not-allowed', TheProductReviewNotAllowed);
app.component('the-product-images-carousel', TheProductImagesCarousel);
app.component('the-collections-carousel', TheCollectionsCarousel);
app.component('the-product-for-gtm', TheProductForGTM);
app.component('the-reconditionned-products', TheReconditionnedProducts);
app.component('the-informations-disclosure', TheInformationsDisclosure);
app.component('cart-wrapper', CartWrapper);
app.component('fidelity-points-usage-popover', FidelityPointsUsagePopover);
app.component('the-aftersale-create', TheAftersaleCreate);
app.component('the-aftersale-follow-up', TheAftersaleFollowUp);
app.component('aftersale-list-item', AftersaleListItem);
app.component('aftersale-list-eligible-item', AftersaleListEligibleItem);
app.component('the-empty-wishlist', TheEmptyWishlist);
app.component('the-empty-orders', TheEmptyOrders);
app.component('the-resume-quote', TheResumeQuote);
app.component('the-notification', TheNotification);
app.component('the-faq-categories', TheFaqCategories);
app.component('the-faq-category', TheFaqCategory);
app.component('pro-form', ProForm);
app.component('v-payment-method', defineAsyncComponent(() => import('./components/footer/paymentMethod.vue')));
app.component('the-product-review-medias', TheProductReviewMedias);
app.component('the-product-review-medias-dialog', TheProductReviewMediasDialog);
app.component('v-alert-success', VAlertSuccess);

/**
 * Data provided by layouts
 *
 */
const dataApp = document.getElementById('app');
translate.setTranslations(document.getElementById('translations').textContent);
setLaravelRoutes(dataApp.dataset.jsRoutes);

/**
 * Sentry setup
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/vue/
 */
Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_LARAVEL_DSN,
  integrations: [],
  debug: false,
});

/**
 * Axios setup
 *
 * @see https://axios-http.com/docs/interceptors
 */
Axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

/**
 * Provide values that can be injected in all descendant
 *
 * @see https://vuejs.org/api/application#app-provide
 */
app.provide('country', dataApp.dataset.country);
app.provide('translate', translate);
app.provide('locale', dataApp.dataset.locale);
app.provide('fallbackLocale', dataApp.dataset.fallback_locale);
app.provide('imageProxyUrl', dataApp.dataset.image_proxy_url);
app.provide('logo', dataApp.dataset.logo);
app.provide('shops', dataApp.dataset.shops);

/**
 * Plugins installation
 *
 * @see https://vuejs.org/api/application#app-use
 */
app.use(LaravelRoutes);
app.use(VueQueryPlugin);

/**
 * Custom Directives
 *
 * @see https://vuejs.org/guide/reusability/custom-directives
 */
app.directive('lazy-load', lazyLoad);
app.directive('scroll-end', scrollEnd);

app.mount('#app');
